import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';

const PAGE_SIZE = 20;

const LicensesList = () => {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tradeLicenseApi.getLicenses({
        search: searchQuery,
        page,
        page_size: PAGE_SIZE,
      });
      const data = response.data;
      setLicenses(data.results || data || []);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (err) {
      console.error('Error fetching licenses:', err);
      setLicenses([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const handleSearch = () => {
    setPage(1);
    fetchLicenses();
  };

  return (
    <div className="applications-list-container">
      <div className="list-header">
        <h1>📜 Issued Licenses</h1>
      </div>

      <div className="filters-card">
        <input
          type="text"
          placeholder="Search by License No. or Business Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="search-input"
          style={{ padding: '8px', width: '300px', marginRight: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <button className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading licenses...</div>
      ) : licenses.length === 0 ? (
        <div className="no-data" style={{ padding: '20px', textAlign: 'center', background: '#fff', borderRadius: '8px' }}>
          No licenses found.
        </div>
      ) : (
        <>
          <div className="table-responsive card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>License No</th>
                  <th>Business Name</th>
                  <th>Owner Name</th>
                  <th>Valid Till</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.id}>
                    <td><strong>{license.license_number}</strong></td>
                    <td>{license.business_name_en}</td>
                    <td>{license.owner_name_en || license.owner_name_hi || '—'}</td>
                    <td>{new Date(license.valid_till).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge status-${license.status}`}>
                        {license.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/office/applications/${license.application}`)}
                        style={{ marginRight: '8px' }}
                      >
                        👁 View Application
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/office/licenses/certificate/${license.license_number}`)}
                      >
                        📜 Certificate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ListPagination
            count={pagination.count}
            next={pagination.next}
            previous={pagination.previous}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default LicensesList;
